import { NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { MatDivider } from "@angular/material/divider";

import { LogService, LOG_MESSAGE_SOURCE, LOG_LINKS_SOURCE } from "./services/log.service";
import AppComponent from "./app.component";
import { SenderComponent } from "./components/sender/sender.component";
import { LinksComponent } from "./components/links/links.component";
import { SettingsComponent } from "./components/settings/settings.component";
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelContent,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import { FormsModule } from "@angular/forms";
import { MatError, MatFormField, MatLabel } from "@angular/material/form-field";
import { ConfigService } from "./services/config.service";
import { MatList, MatListItem, MatListSubheaderCssMatStyler } from "@angular/material/list";
import { MatButton, MatButtonModule } from "@angular/material/button";
import { MatInput } from "@angular/material/input";
import { MessageService } from "./services/message.service";
import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, HttpClientModule } from "@angular/common/http";
import { RiskAssessmentService } from "./services/risk-assessment.service";
import { WaitService } from "./services/wait.service";
import { DesignationService } from "./services/designation.service";
import { AboutComponent } from "./components/about/about.component";
import { DomainDialog } from "./components/domain-dialog/domain-dialog.component";
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle } from "@angular/material/dialog";
import { ActionBarComponent } from "./components/action-bar/action-bar.component";
import { MatTooltip } from "@angular/material/tooltip";
import { MatTab, MatTabContent, MatTabGroup, MatTabLabel } from "@angular/material/tabs";
import { MatIcon, MatIconModule } from "@angular/material/icon";
import { OverviewComponent } from "./components/overview/overview.component";
import { DomainService } from "./services/domain.service";
import { SharedService } from "./services/shared.service";
import { AttachmentService } from "./services/attachment.service";
import { LinksService } from "./services/links.service";
import { AttachmentComponent } from "./components/attachment/attachment.component";
import { ContentComponent } from "./components/content/content.component";
import { MatProgressSpinner, MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { ErrorInterceptor } from "./interceptors/error.interceptor";
import { EvaluatorService } from "./services/evaluator.service";
import { LogsComponent } from "./components/logs/logs.component";
import { ToastrModule } from "ngx-toastr";
import { ScrollToSectionService } from "./services/scrollToSection.service";
import { CommonModule } from "@angular/common";
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalModule,
  MsalService
} from "@azure/msal-angular";
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication
} from "@azure/msal-browser";
import { environment } from "../environments/environment";
import { OfficeLoginService } from "./services/officeLogin.service";
import { OfficeOutlookApiInterceptor } from "./interceptors/office.outlook.api.interceptor";
import { ImageService } from "./services/image.service";
import { QRCodeService } from "./services/qrCode.service";
import { AddTrustedDomainDialog } from "./components/add-trusted-domain/add-trusted-domain.dialog";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { TrustedDomainsComponent } from "./components/trusted-domains/trusted-domains.component";
import { MatProgressBar } from "@angular/material/progress-bar";
import { MatOption, MatSelect } from "@angular/material/select";
import { TermsAndConditionsComponent } from "./components/terms-and-conditions/terms-and-conditions.component";
import { ConfirmationDialogComponent } from "./components/settings/confirmation-dialog/confirmation-dialog.component";
import { SettingsService } from "./services/settings.service";
import { ResponseInterceptor } from "./interceptors/response.interceptor";

export function HttpLoaderFactory(http: HttpBackend) {
    return new TranslateHttpLoader(new HttpClient(http));
}

export function loggerCallback() {
  // console.log(message);
}
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: '/',
      postLogoutRedirectUri: '/'
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Popup,
    authRequest: {
      scopes: [...environment.apiConfig.scopes]
    },
    // loginFailedRoute: '/login-failed'
  };
}

@NgModule({
  declarations: [
    AppComponent,
    SettingsComponent,
    SenderComponent,
    LinksComponent,
    AttachmentComponent,
    ContentComponent,
    AboutComponent,
    DomainDialog,
    ActionBarComponent,
    OverviewComponent,
    LogsComponent,
    AddTrustedDomainDialog,
    TrustedDomainsComponent,
    TermsAndConditionsComponent,
    ConfirmationDialogComponent
  ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        MatMenu,
        MatDivider,
        MatMenuTrigger,
        MatMenuItem,
        MatAccordion,
        MatExpansionPanel,
        MatExpansionPanelTitle,
        MatExpansionPanelHeader,
        FormsModule,
        MatError,
        MatFormField,
        MatList,
        MatListSubheaderCssMatStyler,
        MatListItem,
        MatButton,
        MatInput,
        HttpClientModule,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatDialogClose,
        MatTooltip,
        MatTabGroup,
        MatTab,
        MatIcon,
        MatTabLabel,
        MatIconModule,
        MatProgressSpinner,
        MatButtonModule,
        MatTabContent,
        MatLabel,
        CommonModule,
        MatProgressSpinnerModule,
        MatProgressBar,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpBackend]
            }
        }),
        ToastrModule.forRoot({
            preventDuplicates: true,
            positionClass: "toast-bottom-right"
        }),
        MsalModule.forRoot(
            new PublicClientApplication({
                auth: {
                    clientId: environment.msalConfig.auth.clientId,
                    authority: environment.msalConfig.auth.authority,
                    redirectUri: "/",
                    postLogoutRedirectUri: "/"
                },
                cache: {
                    cacheLocation: BrowserCacheLocation.LocalStorage
                },
                system: {
                    allowNativeBroker: false,
                    loggerOptions: {
                        loggerCallback,
                        logLevel: LogLevel.Info,
                        piiLoggingEnabled: false
                    }
                }
            }),
            {
                interactionType: InteractionType.Popup,
                authRequest: {
                    scopes: environment.apiConfig.scopes
                }
            },
            {
                interactionType: InteractionType.Popup, // MSAL Interceptor Configuration
                protectedResourceMap: new Map([
                    [environment.apiConfig.microsoft_graph_url_personal, environment.apiConfig.scopes],
                    [environment.apiConfig.microsoft_graph_url_personal + "/messages", environment.apiConfig.scopes]
                ])
            }
        ),
        MatExpansionPanelContent,
        MatSelect,
        MatOption
    ],
  providers: [
    { provide: LOG_MESSAGE_SOURCE, useExisting: MessageService },
    { provide: LOG_LINKS_SOURCE, useExisting: LinksService },
    LogService, ConfigService, MessageService, RiskAssessmentService, WaitService, DesignationService, DomainService,
    SharedService, AttachmentService, LinksService, EvaluatorService, ScrollToSectionService, ImageService,
    OfficeLoginService, QRCodeService, SettingsService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OfficeOutlookApiInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ResponseInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService
  ],
  bootstrap: [AppComponent]
})

export default class AppModule {

}
